import { desktopEndpoints } from '@/utils/endpoints'
import { defineStore } from 'pinia'
import {
  FILE_FROM_SERVER,
  INQUIRIES,
  INQUIRY_COMMENT,
  INQUIRY_FILTERS,
  INQUIRY_ITEM,
  INQUIRY_STATUS,
  Document,
  INQUIRY_TYPE,
} from '../types'

import { showFormattedInquiryCreatedAt } from '@/utils/matchers'

import { addMinutes, parseISO, set, differenceInHours } from 'date-fns'

type StandardResponseTypes =
  | 'All Set'
  | 'Ask for Confirmation'
  | 'Ask for Info'
  | 'Decline'
  | 'No Delivery'
  | 'Offer Alternative'
  | 'From Scratch'
  | 'AutoResponse'
  | 'Send Info'

interface DesktopAttachments
  extends Record<StandardResponseTypes, FILE_FROM_SERVER[]> {}

interface DesktopDocuments extends Record<StandardResponseTypes, Document[]> {}

const makeEmptyObject = () => ({
  'All Set': [],
  'Ask for Confirmation': [],
  'Ask for Info': [],
  Decline: [],
  'No Delivery': [],
  'Offer Alternative': [],
  'From Scratch': [],
  AutoResponse: [],
  'Send Info': [],
})

export const useDesktopDetailsStore = defineStore('desktopDetails', () => {
  const authStore = useAuthStore()
  const appStore = useAppStore()

  const inquiry: Ref<INQUIRY_ITEM | undefined> = ref()
  const inquiries: Ref<INQUIRIES> = ref([])
  const searchInquiries: Ref<INQUIRIES> = ref([])
  const comments: Ref<INQUIRY_COMMENT[]> = ref([])
  const selectedInquiryStatus: Ref<INQUIRY_STATUS | undefined> = ref()
  const attachments = ref<DesktopAttachments>(makeEmptyObject())
  const documents = ref<DesktopDocuments>(makeEmptyObject())
  const reusableAttachments = ref<FILE_FROM_SERVER[]>([])
  const proposalUserEmailComment = ref('')
  const autoResponseComment: Ref<string> = ref()
  const autoResponseStatus: Ref<INQUIRY_STATUS | undefined> = ref()
  const aiResponses: Ref<AI_RESPONSE[]> = ref([])
  const standardResponses = ref()
  const responsesType = ref('ai')
  const currentResponseTemplate = ref('From Scratch')
  const initialDataReady = ref(false)
  const showDetailsSurvey = ref(false)
  const followUp = ref(false) //hide standard and ai responses
  const openArrangePickupModal = ref(false)
  const openAcceptOrderModal = ref(false)
  const paginate = reactive({
    page: 1,
    pageSize: 10,
    total: 150,
  })
  const filter = ref('Upcoming')
  const fetchingInquiries = ref(false)
  const searchQuery = ref('')
  const statusFilter: Ref<INQUIRY_STATUS> = ref(null)
  const sortFilter: Ref<string | number | null> = ref('')
  const selectAllInquiries = ref(false)
  const selectedInquiryIds: Ref<number[]> = ref([])
  const updatingInquiry = ref(false)

  function setInquiry(inq: INQUIRY_ITEM) {
    inquiry.value = inq
  }

  function setInquiryComments(inquiryComments: INQUIRY_COMMENT[]) {
    comments.value = inquiryComments
    followUp.value =
      (inquiryComments.length > 0 && !inquiry.value.autoresponse_sent) ||
      (inquiryComments.length > 1 && inquiry.value.autoresponse_sent)
  }

  async function updateInquiry(updatedInquiry: INQUIRY_ITEM) {
    updatingInquiry.value = true
    const { data } = await useApi(
      `${desktopEndpoints().updateInquiry(authStore.spotId, updatedInquiry.id)}`,
    )
      .post(updatedInquiry)
      .json()

    updatingInquiry.value = false

    inquiry.value = data.value.inquiries[0]
    const listIndex = inquiries.value.findIndex(
      (i) => i.id === updatedInquiry.id,
    )
    if (listIndex !== -1) inquiries.value[listIndex] = data.value.inquiries[0]
    if (
      inquiry.value.type == 'Private Party' ||
      inquiry.value.type == 'Catering'
    ) {
      followUp.value =
        (comments.value.length > 0 && !inquiry.value.autoresponse_sent) ||
        (comments.value.length > 1 && inquiry.value.autoresponse_sent)
    }
    if (inquiries.value.length) {
      const idx = inquiries.value.findIndex((i) => i.id === inquiry.value.id)
      if (idx !== -1) inquiries.value[idx] = inquiry.value
    }
  }

  async function updateDoorDashDeliveryInquiry(
    updatedInquiry: INQUIRY_ITEM,
    scheduledIn: string,
    pickupDriverInstructions: string,
  ) {
    updatedInquiry.schedule_delivery_in = scheduledIn
    updatedInquiry.pick_up_instructions = pickupDriverInstructions
    updatingInquiry.value = true

    const { data } = await useApi(
      `${desktopEndpoints().updateInquiry(authStore.spotId, updatedInquiry.id)}`,
    )
      .post(updatedInquiry)
      .json()

    updatingInquiry.value = false

    inquiry.value = data.value.inquiries[0]
  }

  async function deleteInquiry(inquiryId: number) {
    await useApi(
      `${desktopEndpoints().deleteInquiry(authStore.spotId, inquiryId)}`,
    ).delete()
  }

  async function fetchInquiryById(inquiryId: string) {
    const { data } = await useApi(
      `${desktopEndpoints().getInquiry(authStore.spotId, inquiryId)}`,
    ).json()

    if (data.value?.inquiries) {
      setInquiry(data.value.inquiries[0])
      return data.value.inquiries[0]
    }

    return null
  }

  async function fetchInquiryComments(inquiryId: string) {
    const { data } = await useApi(
      `${desktopEndpoints().getInquiryComments(authStore.spotId, inquiryId)}`,
    ).json()
    setInquiryComments(data.value.inquiry_comments)
  }

  function shouldFetchAutoResponseData(inquiryType: string): boolean {
    return (
      ['Catering', 'Private Party'].includes(inquiryType) &&
      (comments.value.length < 1 ||
        (comments.value.length === 1 && inquiry.value.autoresponse_sent))
    )
  }

  async function createInquiryComment(
    inquiryId: number,
    comment: string,
    responseType: string,
    sendTextMessageUpdate?: boolean,
    temporaryAttachments?: FILE_FROM_SERVER[],
    temporaryDocuments?: Document[],
    aiResponseType?: string | null,
  ) {
    const attachment_ids = !appStore.hasEventPlanner
      ? []
      : temporaryAttachments
        ? temporaryAttachments.map((file) => file.id)
        : []

    const document_ids = !appStore.hasEventPlanner
      ? []
      : temporaryDocuments
        ? temporaryDocuments.map((file) => file.id)
        : []

    const { data } = await useApi(
      `${desktopEndpoints().createInquiryComment(authStore.spotId, inquiryId)}`,
    )
      .post({
        text: comment,
        response_type: responseType,
        attachments: attachment_ids,
        documents: document_ids,
        send_text_message: sendTextMessageUpdate ?? inquiry?.send_text_message,
        ai_response_type: aiResponseType,
      })
      .json()

    if (responseType === 'Manual auto') {
      Object.keys(attachments.value).forEach((key) => {
        attachments.value[key] = getAttachmentsByTemplate(key)
      })
      documents.value = makeEmptyObject() as DesktopDocuments
    }
    return data.value.inquiry_comments[0]
  }

  function setSelectedInquiryStatus(inquiryStatus: INQUIRY_STATUS | undefined) {
    selectedInquiryStatus.value = inquiryStatus
  }

  const fetchInquiryProposal = async (inquiryId: number) => {
    try {
      const { data } = await useApi(
        `${desktopEndpoints().getProposal(authStore.spotId, inquiryId)}`,
      ).json()

      if (data && data.value) {
        return data.value.linked
      }
      return null
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        console.error('Resource not found (404)')
      } else {
        console.error('An error occurred', error)
      }

      return null
    }
  }

  const createInquiryProposal = async (body: object) => {
    const { data } = await useApi(
      `${desktopEndpoints().createProposal(authStore.spotId)}`,
    )
      .post(body)
      .json()

    if (data) {
      if (data.value != null) return data.value.linked
    }

    return null
  }

  const editInquiryProposal = async (
    proposalPaymentID: string,
    body: object,
  ) => {
    const { data } = await useApi(
      `${desktopEndpoints().editProposal(authStore.spotId, proposalPaymentID)}`,
    )
      .post(body)
      .json()

    if (data) {
      if (data.value != null) return data.value.linked
    }

    return null
  }

  const sendProposalAttachment = async (proposalID: string, file: FormData) => {
    await useApi(
      `${desktopEndpoints().createProposalAttachment(authStore.spotId)}?object_type=proposals&object_id=${proposalID}`,
    )
      .post(file)
      .json()
  }
  const getAttachmentForProposal = async (proposalId: number) => {
    const { data } = await useApi(
      `${desktopEndpoints().getInquiryAttachments(authStore.spotId)}?object_type=proposals&object_id=${proposalId}`,
    ).json()
    return data.value.attachments
  }

  const sendProposal = async (
    inquiryId: number,
    comment: string,
    proposalId: number,
  ) => {
    const attachments = await getAttachmentForProposal(proposalId)
    const currentFilesOnServerIDs: number[] = []
    if (attachments) {
      attachments.map((file: FILE_FROM_SERVER) =>
        currentFilesOnServerIDs.push(file.id),
      )
    }

    const { data } = await useApi(
      `${desktopEndpoints().createInquiryComment(authStore.spotId, inquiryId)}`,
    )
      .post({
        text: comment,
        attachments: currentFilesOnServerIDs,
        proposal_id: proposalId,
        responseType: 'manual',
      })
      .json()
    return data.value
  }

  function getInquiryEndTime(inquiry: INQUIRY_ITEM) {
    if (showFormattedInquiryCreatedAt(inquiry.type)) {
      return parseISO(inquiry.created_at)
    }

    const currentInqTime = set(new Date(), {
      year: inquiry.year!,
      month: inquiry.month! - 1,
      date: inquiry.day!,
      hours: inquiry.hour!,
      minutes: inquiry.min!,
      seconds: 0,
    })

    return addMinutes(currentInqTime as Date, inquiry.duration_min! ?? 0)
  }

  function isLastCommentPast24hrs(inquiry: INQUIRY_ITEM) {
    return (
      inquiry.last_comment &&
      differenceInHours(new Date(), parseISO(inquiry.last_comment.created_at), {
        roundingMethod: 'ceil',
      }) > 24
    )
  }

  function shouldRenderAutoResponse(inquiry: INQUIRY_ITEM) {
    if (inquiry.last_comment?.author_role === 'customer') {
      return false
    }
    return true
  }

  async function fetchAiRespones(inquiryId: string) {
    const { data } = await useApi(
      `${desktopEndpoints().getAiResponses(authStore.spotId, inquiryId)}`,
    ).json()

    aiResponses.value = (data.value?.inquiry_ai_responses ??
      []) as AI_RESPONSE[]

    responsesType.value = aiResponses.value?.length > 0 ? 'ai' : 'standard'

    return null
  }

  function getAttachmentsByTemplate(templateName: string) {
    const attachments =
      standardResponses.value.find((e: any) => e.template_name === templateName)
        ?.links?.attachments ?? []

    return attachments
      .map((e: any) => (e.values !== undefined ? e.values : e))
      .flat()
  }

  async function fetchStandardRespones(inquiryType: string, inquiryId: string) {
    const order = [
      'Ask for Confirmation',
      'All Set',
      'Offer Alternative',
      'Ask for Info',
      'Send Info',
      'Decline',
    ]

    const rank = Object.fromEntries(order.map((name, i) => [name, i]))

    function sortByTemplateName(items) {
      return [...items].sort((a, b) => {
        const ra = rank[a.template_name] ?? Infinity
        const rb = rank[b.template_name] ?? Infinity
        if (ra !== rb) return ra - rb
        return String(a.template_name).localeCompare(String(b.template_name))
      })
    }

    const { data } = await useApi(
      `${desktopEndpoints().getStandardInquiryAutoResponses(authStore.spotId, inquiryType, inquiryId)}`,
    ).json()

    standardResponses.value = sortByTemplateName(
      data.value.standard_inquiry_responses,
    )

    if (appStore.hasEventPlanner) {
      Object.keys(attachments.value).forEach((key) => {
        attachments.value[key] = getAttachmentsByTemplate(key)
      })
    }

    return null
  }

  async function fetchAutoResponseComment(inquiryId: string) {
    const { data } = await useApi(
      `${desktopEndpoints().getAutoResponseComment(authStore.spotId, inquiryId)}`,
    ).json()

    autoResponseComment.value = data.value.autoresponse_comment ?? ''

    return null
  }

  async function fetchAutoResponseAttachments(inquiryType: string) {
    const attachmentType =
      inquiryType === 'Private Party'
        ? 'private_parties_autoresponse'
        : 'catering_autoresponse'
    const objectId =
      inquiryType === 'Private Party'
        ? appStore.spotInfo.private_parties_settings.id
        : appStore.spotInfo.catering_settings.id

    const { data } = await useApi(
      `${desktopEndpoints().getInquiryAttachments(Number(authStore.spotId))}?object_type=${attachmentType}&object_id=${objectId}`,
    ).json()

    attachments.value.AutoResponse = data.value.attachments

    return null
  }

  async function setInitialData(inquiryType: string, inquiryId: string) {
    await fetchInquiryById(inquiryId)
    await fetchInquiryComments(inquiryId)
    if (['Catering', 'Private Party'].includes(inquiryType)) {
      await fetchStandardRespones(inquiryType, inquiryId)
      await fetchAiRespones(inquiryId)
      await fetchAutoResponseComment(inquiryId)
      if (appStore.hasEventPlanner)
        await fetchAutoResponseAttachments(inquiryType)
    } else {
      followUp.value = true
    }

    initialDataReady.value = true
  }

  function setFollowUp(value: boolean) {
    followUp.value = value
  }

  function setOpenArrangePickupModal(value: boolean) {
    openArrangePickupModal.value = value
  }

  const setOpenAcceptOrderModal = (value: boolean) => {
    openAcceptOrderModal.value = value
  }

  function setInquiries(allInquiries: INQUIRIES) {
    inquiries.value = allInquiries
  }

  async function fetchInquiries(inquiryType: INQUIRY_FILTERS['inquiryType']) {
    fetchingInquiries.value = true
    resetSelectedInquiries()
    const { data } = await useApi(
      `${desktopEndpoints().inquiriesList(authStore.spotId)}/?inquiryType=${inquiryType}&filters=${filter.value}&sort=${sortFilter.value}&page=${paginate.page}&pageSize=${paginate.pageSize}${searchQuery.value.length ? '&searchQuery=' + searchQuery.value : ''}${statusFilter.value?.length ? '&status=' + statusFilter.value : ''}`,
    ).json()

    const inquiries = data.value?.inquiries ?? []
    paginate.total = data.value?.total ?? 0

    setInquiries(inquiries)
    fetchingInquiries.value = false
    return inquiries
  }

  function updatePagination(page: number, pageSize: number, total: number) {
    paginate.page = page
    paginate.pageSize = pageSize
    paginate.total = total
    resetSelectedInquiries()
  }

  async function seeInquiries() {
    const { data } = await useApi(
      `${desktopEndpoints().seeInquiries(authStore.spotId)}`,
    )
      .post({ ids: selectedInquiryIds.value })
      .json()

    if (data.value?.status == 'success') {
      if (filter.value === 'Unopened') {
        inquiries.value = inquiries.value.filter(
          (inq) => !selectedInquiryIds.value.includes(inq.id),
        )
      }
      
      inquiries.value = inquiries.value.map((inq) => {
        if (selectedInquiryIds.value.includes(inq.id)) {
          return {
            ...inq,
            inquiry_opened_at: new Date().toString(),
            get_num_of_unread_emails: 0,
            last_comment: {
              ...inq.last_comment,
              seen_at: new Date().toString(),
            },
          }
        }

        return inq
      })
    }

    bridge.sendMessage({
      name: 'see_inquiries',
      payload: { inquiryIds: [...selectedInquiryIds.value] },
    })
    resetSelectedInquiries()
  }

  async function unseeInquiries() {
    const { data } = await useApi(
      `${desktopEndpoints().unseeInquiries(authStore.spotId)}`,
    )
      .post({ ids: selectedInquiryIds.value })
      .json()

    if (data.value.status == 'success') {
      inquiries.value = inquiries.value.map((inq) => {
        if (selectedInquiryIds.value.includes(inq.id)) {
          return {
            ...inq,
            inquiry_opened_at: null,
          }
        }

        return inq
      })
    }

    bridge.sendMessage({
      name: 'unsee_inquiries',
      payload: { inquiryIds: [...selectedInquiryIds.value] },
    })

    resetSelectedInquiries()
  }

  async function archiveInquiries(inquiryType: INQUIRY_TYPE) {
    const { data } = await useApi(
      `${desktopEndpoints().archiveInquiries(authStore.spotId)}`,
    )
      .post({ ids: selectedInquiryIds.value })
      .json()

    bridge.sendMessage({
      name: 'see_inquiries',
      payload: { inquiryIds: [...selectedInquiryIds.value] },
    })

    inquiries.value = inquiries.value.filter(
      (inq) => !selectedInquiryIds.value.includes(inq.id),
    )

    resetSelectedInquiries()
    if (paginate.page > 1 && inquiries.value.length === 0) {
      paginate.page -= 1
    } else if (paginate.page === 1 && inquiries.value.length === 0) {
      fetchInquiries(inquiryType)
    }
  }

  function resetSelectedInquiries() {
    selectAllInquiries.value = false
    selectedInquiryIds.value = []
  }

  async function refreshListPage(inquiryType: INQUIRY_TYPE) {
    resetSelectedInquiries()
    setInquiries([])
    await fetchInquiries(inquiryType)
  }

  return {
    inquiry,
    inquiries,
    reusableAttachments,
    setInquiry,
    attachments,
    documents,
    updateInquiry,
    deleteInquiry,
    fetchInquiryById,
    createInquiryComment,
    setInquiryComments,
    comments,
    setSelectedInquiryStatus,
    selectedInquiryStatus,
    fetchInquiryProposal,
    editInquiryProposal,
    createInquiryProposal,
    sendProposalAttachment,
    getAttachmentForProposal,
    sendProposal,
    proposalUserEmailComment,
    autoResponseStatus,
    getInquiryEndTime,
    isLastCommentPast24hrs,
    shouldRenderAutoResponse,
    fetchStandardRespones,
    setInitialData,
    aiResponses,
    initialDataReady,
    standardResponses,
    responsesType,
    currentResponseTemplate,
    autoResponseComment,
    followUp,
    updateDoorDashDeliveryInquiry,
    setFollowUp,
    showDetailsSurvey,
    openArrangePickupModal,
    setOpenArrangePickupModal,
    openAcceptOrderModal,
    setOpenAcceptOrderModal,
    fetchInquiries,
    setInquiries,
    paginate,
    filter,
    updatePagination,
    fetchingInquiries,
    searchInquiries,
    searchQuery,
    seeInquiries,
    unseeInquiries,
    statusFilter,
    sortFilter,
    archiveInquiries,
    selectAllInquiries,
    selectedInquiryIds,
    resetSelectedInquiries,
    refreshListPage,
    updatingInquiry,
  }
})
